import SkillBox from "./skillBox";
import { useTranslation } from "react-i18next";
import icons from '../constants/icons';

function HeroSection() {
    const { t } = useTranslation();

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = '/Ali Ameur.pdf';
        link.download = 'Ali Ameur.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const skills = [
        { alt: "java", icon: icons.java, skill: "Java" },
        { alt: "spring", icon: icons.spring, skill: "Spring Boot" },
        { alt: "rabbit", icon: icons.rabbit, skill: "RabbitMQ" },
        { alt: "postgres", icon: icons.postgres, skill: "PostgreSQL" },
        { alt: "docker", icon: icons.docker, skill: "Docker" },
        { alt: "nginx", icon: icons.nginx, skill: "Nginx" },

        { alt: "react", icon: icons.react, skill: "React" },
        { alt: "tailwind", icon: icons.tailwind, skill: "Tailwind CSS" },
        { alt: "node", icon: icons.node, skill: "Node.js" },
        { alt: "express", icon: icons.express, skill: "Express.js" },
        { alt: "git", icon: icons.git, skill: "Git" },
        { alt: "sonar", icon: icons.sonar, skill: "SonarQube" },

        { alt: "junit", icon: icons.junit, skill: "JUnit" },
        { alt: "react-native", icon: icons.react, skill: "React Native" },
        { alt: "expo", icon: icons.expo, skill: "Expo" },
        { alt: "mongodb", icon: icons.mongodb, skill: "MongoDB" },
    ];

    return (
        <section className="relative overflow-hidden bg-base-200">
            {/* Animated background shapes */}
            <div className="absolute inset-0 overflow-hidden opacity-20">
                <div className="absolute top-1/4 -left-20 w-64 h-64 bg-primary rounded-full filter blur-3xl animate-blob"></div>
                <div className="absolute top-1/2 right-0 w-72 h-72 bg-secondary rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-20 left-1/4 w-60 h-60 bg-accent rounded-full filter blur-3xl animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-24">
                {/* Text Content - Centered */}
                <div className="text-base-content space-y-3 md:space-y-8 text-center animate-fadeInUp">
                    <div className="flex items-center gap-3 justify-center">
                        <div className="w-3 h-3 rounded-full bg-accent animate-pulse"></div>
                        <span className="md:text-lg font-medium text-accent ">
                            {t('hero.status')}
                        </span>
                    </div>

                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                        {t('hero.greeting')} <span className="text-primary">{t('hero.name')}</span>
                    </h1>

                    <div className="max-w-3xl mx-auto mt-6">
                        <p className="text-lg md:text-xl leading-8 text-base-content/80 text-center">
                            {t('hero.intro')}{" "}
                            <span className="">{/* font-semibold text-primary */}
                                {t('hero.highlight')}
                            </span>{" "}
                            {t('hero.specialization')}
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-10 py-6">

                        <div className="text-center">
                            <h3 className="text-3xl font-bold text-primary">3+</h3>
                            <p className="text-sm uppercase tracking-wider text-base-content/60">
                                Years Experience
                            </p>
                        </div>

                        <div className="w-px bg-base-300 hidden md:block" />

                        <div className="text-center">
                            <h3 className="text-3xl font-bold text-primary">Java</h3>
                            <p className="text-sm uppercase tracking-wider text-base-content/60">
                                Spring Boot · Jakarta EE
                            </p>
                        </div>

                        <div className="w-px bg-base-300 hidden md:block" />

                        <div className="text-center">
                            <h3 className="text-3xl font-bold text-primary">Backend</h3>
                            <p className="text-sm uppercase tracking-wider text-base-content/60">
                                Microservices · REST APIs
                            </p>
                        </div>

                    </div>

                    <div className="flex flex-wrap gap-4 mt-6 justify-center">
                        <a
                            href="#contact"
                            className="btn btn-primary md:btn-lg px-8 hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl group"
                        >
                            {t('hero.contactButton')}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </a>
                        <button
                            onClick={handleDownload}
                            className="btn btn-outline md:btn-lg px-8 border-base-content text-base-content hover:bg-base-content hover:text-base-100 transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-lg group"
                        >
                            {t('hero.downloadButton')}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Skills Section */}
                <div className="mt-20 w-full">
                    <div className="flex flex-wrap justify-center gap-3 md:gap-4">
                        {skills.map(({ alt, icon, skill, link }) => (
                            <SkillBox
                                key={alt}
                                alt={alt}
                                icon={icon}
                                skill={skill}
                                link={link}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HeroSection;